import type { NamedExoticComponent, PropsWithChildren, ReactText, ReactNode } from 'react'
import { memo } from 'react'
import { getIntl, getLocale } from 'umi'

import Placeholder from '@/components/Placeholder'
import type { IPlaceholderProps } from '@/components/Placeholder'

type IErrorFn = () => Error | ReactText

type IErrorType = Error | ReactText | IErrorFn | IntlTextType

interface IErrorProps extends IPlaceholderProps {
  error?: Error | ReactText | IErrorFn | IntlTextType
  subMsg?: ReactNode
  btnName?: IntlTextType
  handleClick?: () => void
  className?: string
  childClassName?: string
}

const errorMessageHandler: (error: IErrorType) => ReactText = (error) => {
  let err = error
  if (['boolean', 'string', 'number'].includes(typeof error)) return error as ReactText
  if (error.constructor.name === 'Error') {
    err = error as Error
    return err.message || err.toString()
  }
  if (typeof error === 'function') {
    const errorFn = error as () => Error | ReactText
    err = errorFn()
    return errorMessageHandler(err)
  }
  return error?.toString()
}

const Error: NamedExoticComponent<PropsWithChildren<IErrorProps>> = memo((props) => {
  const intl = getIntl(getLocale())

  const {
    error,
    btnName = intl.messages['Global.retry'],
    children,
    childClassName,
    className,
    ...rest
  } = props

  if (error)
    return (
      <Placeholder
        msg={errorMessageHandler((error as Error)?.message || error)}
        btnName={btnName}
        wrapperClassName={className}
        {...rest}
      />
    )
  return <>{childClassName ? <div className={childClassName}>{children}</div> : children}</>
})

export type { IErrorType, IErrorProps }

export default Error
